import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button, Badge, TextArea, Icon } from '@folio/stripes-components';

const ORIGIN_COLORS = {
  extracted: '#E3F2FD',
  retrieved: '#E8F5E9',
  suggested: '#FFF8E1',
  policy:    '#F3E5F5',
};

const ORIGIN_LABELS = {
  extracted: 'Extracted',
  retrieved: 'Retrieved',
  suggested: 'Suggested',
  policy:    'Policy',
};

const CONFIDENCE_COLORS = {
  high:   '#4CAF50',
  medium: '#FF9800',
  low:    '#F44336',
};

const FieldProposalCard = ({ proposal, onDecision }) => {
  const [decision, setDecision]       = useState(null);
  const [modifiedValue, setModified]  = useState('');
  const [showModify, setShowModify]   = useState(false);

  const handleAccept = () => {
    setDecision('accept');
    setShowModify(false);
    onDecision({ proposalId: proposal.id, decision: 'accept' });
  };

  const handleReject = () => {
    setDecision('reject');
    setShowModify(false);
    onDecision({ proposalId: proposal.id, decision: 'reject' });
  };

  const handleModify = () => {
    if (!modifiedValue.trim()) return;
    setDecision('modify');
    setShowModify(false);
    onDecision({ proposalId: proposal.id, decision: 'modify', modifiedValue: modifiedValue.trim() });
  };

  const displayValue = typeof proposal.proposedValue === 'object'
    ? JSON.stringify(proposal.proposedValue, null, 2)
    : String(proposal.proposedValue ?? '');

  const bgColor = decision === 'accept' ? '#E8F5E9'
    : decision === 'reject' ? '#FFEBEE'
    : decision === 'modify' ? '#FFF8E1'
    : '#fff';

  return (
    <div style={{
      border:       '1px solid #ddd',
      borderRadius: '6px',
      padding:      '12px',
      marginBottom: '8px',
      backgroundColor: bgColor,
      transition:   'background-color 0.2s',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div>
          <span style={{ fontWeight: 600, fontSize: '13px' }}>{proposal.fieldPath}</span>
          <span style={{
            marginLeft: '8px', padding: '2px 6px', borderRadius: '10px',
            fontSize: '11px', backgroundColor: ORIGIN_COLORS[proposal.origin] ?? '#eee',
          }}>
            {ORIGIN_LABELS[proposal.origin] ?? proposal.origin}
          </span>
          {proposal.confidenceLevel && (
            <span style={{ marginLeft: '4px', fontSize: '11px', color: CONFIDENCE_COLORS[proposal.confidenceLevel] }}>
              ● {proposal.confidenceLevel}
            </span>
          )}
        </div>
        {decision && (
          <Badge color={decision === 'accept' ? 'green' : decision === 'reject' ? 'red' : 'orange'}>
            {decision}
          </Badge>
        )}
      </div>

      {/* Proposed value */}
      <div style={{ backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px', fontSize: '13px', marginBottom: '8px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', maxHeight: '80px', overflowY: 'auto' }}>
        {displayValue}
      </div>

      {/* Rationale */}
      {proposal.rationale && (
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', fontStyle: 'italic' }}>
          <Icon icon="info" /> {proposal.rationale}
        </div>
      )}

      {/* Validation issues */}
      {proposal.validationIssues?.length > 0 && (
        <div style={{ marginBottom: '8px' }}>
          {proposal.validationIssues.map((issue, i) => (
            <div key={i} style={{ fontSize: '11px', color: issue.startsWith('ERROR') ? '#C62828' : '#E65100', padding: '2px 0' }}>
              ⚠ {issue}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      {!decision && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <Button buttonStyle="primary" onClick={handleAccept} marginBottom0>
            <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.accept" defaultMessage="Accept" />
          </Button>
          <Button buttonStyle="default" onClick={() => setShowModify(!showModify)} marginBottom0>
            <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.modify" defaultMessage="Modify" />
          </Button>
          <Button buttonStyle="danger" onClick={handleReject} marginBottom0>
            <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.reject" defaultMessage="Reject" />
          </Button>
        </div>
      )}

      {/* Modify input */}
      {showModify && (
        <div style={{ marginTop: '8px' }}>
          <TextArea
            value={modifiedValue}
            onChange={e => setModified(e.target.value)}
            placeholder="Enter modified value..."
            rows={2}
          />
          <Button buttonStyle="primary" onClick={handleModify} marginBottom0 style={{ marginTop: '4px' }}>
            <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.confirmModify" defaultMessage="Confirm" />
          </Button>
        </div>
      )}

      {/* Undo */}
      {decision && (
        <Button buttonStyle="link" onClick={() => { setDecision(null); setShowModify(false); onDecision({ proposalId: proposal.id, decision: null }); }} marginBottom0>
          <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.undo" defaultMessage="Undo" />
        </Button>
      )}
    </div>
  );
};

FieldProposalCard.propTypes = {
  proposal:   PropTypes.object.isRequired,
  onDecision: PropTypes.func.isRequired,
};

export default FieldProposalCard;
